import { LuneTheme } from '@lune-fe/lune-ui-lib'
import CancelIcon from '@mui/icons-material/Cancel'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import Box from '@mui/material/Box'
import Text from '@mui/material/Typography'
import { ReactNode } from 'react'

import { spaceGrotesk } from '@/app/fonts'
import FullScreenOnMobileWrapper from '@/components/FullScreenOnMobileWrapper'
import useDevice from '@/hooks/useDevice'

const { typography, palette } = LuneTheme

const HasFeature = () => {
    return <CheckCircleIcon sx={{ color: 'rgba(5, 139, 44, 1)' }} />
}

const DoesNotHaveFeature = () => {
    return <CancelIcon sx={{ color: 'rgba(255, 0, 0, 1)' }} />
}

const tableContent = [
    {
        feature: { emoji: '🕒', label: 'Integration time' },
        ecotransit: <>1+ month</>,
        lune: <>1 hour</>,
    },
    {
        feature: { emoji: '👨‍💻', label: 'API' },
        ecotransit: <>SOAP</>,
        lune: <>REST</>,
    },
    {
        feature: { emoji: '📋', label: 'Bulk upload via CSV' },
        ecotransit: <HasFeature />,
        lune: <HasFeature />,
    },
    {
        feature: { emoji: '✅', label: 'GLEC certified & ISO compliant' },
        ecotransit: <HasFeature />,
        lune: <HasFeature />,
    },
    {
        feature: { emoji: '🌍', label: 'Multimodal' },
        ecotransit: <HasFeature />,
        lune: <HasFeature />,
    },
    {
        feature: { emoji: '🔁', label: 'Well-to-wheel and gases breakdown' },
        ecotransit: <HasFeature />,
        lune: <HasFeature />,
    },
    {
        feature: { emoji: '⛽', label: 'Custom fuel blends, load factors, and more' },
        ecotransit: <HasFeature />,
        lune: <HasFeature />,
    },
    {
        feature: {
            emoji: '🚚',
            label: 'Road calculations accounting for traffic, EURO engine standard, and more',
        },
        ecotransit: <HasFeature />,
        lune: <HasFeature />,
    },
    {
        feature: { emoji: '✈️', label: 'Fuel-based air freight calculations' },
        ecotransit: <HasFeature />,
        lune: <HasFeature />,
    },
    {
        feature: { emoji: '✈️', label: 'Aircraft specific calculations using flight number' },
        ecotransit: <DoesNotHaveFeature />,
        lune: <HasFeature />,
    },
    {
        feature: { emoji: '🛰️', label: 'AIS tracking for sea distances' },
        ecotransit: <DoesNotHaveFeature />,
        lune: <HasFeature />,
    },
    {
        feature: {
            emoji: '🔍',
            label: 'Automatic vessel-identification based off carrier schedules',
        },
        ecotransit: <DoesNotHaveFeature />,
        lune: <HasFeature />,
    },
    {
        feature: { emoji: '📦', label: 'Cargo emission calculations (products, materials, etc.)' },
        ecotransit: <DoesNotHaveFeature />,
        lune: <HasFeature />,
    },
    {
        feature: { emoji: '📄', label: 'Automatically generated emission report webpage' },
        ecotransit: <DoesNotHaveFeature />,
        lune: <HasFeature />,
    },
    {
        feature: { emoji: '🔍', label: 'Auditable calculation results' },
        ecotransit: <DoesNotHaveFeature />,
        lune: <HasFeature />,
    },
    {
        feature: { emoji: '💚', label: 'High-quality biofuel insetting and carbon offsetting' },
        ecotransit: <DoesNotHaveFeature />,
        lune: <HasFeature />,
    },
    {
        feature: { emoji: '📣', label: 'Go-to-market support' },
        ecotransit: <DoesNotHaveFeature />,
        lune: <HasFeature />,
    },
    {
        feature: { emoji: '🤙', label: 'Responsive customer support' },
        ecotransit: <DoesNotHaveFeature />,
        lune: <HasFeature />,
    },
    {
        feature: { emoji: '✨', label: 'Emission reduction recommendations' },
        ecotransit: <DoesNotHaveFeature />,
        lune: <>Coming soon!</>,
    },
]

const TableRow = ({ children, header }: { children: ReactNode; header?: boolean }) => {
    return (
        <Box
            sx={{
                fontFamily: header ? spaceGrotesk.style.fontFamily : typography.fontFamily,
                fontWeight: header ? 500 : 400,
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '12px',
                minHeight: {
                    xs: '48px',
                    md: '64px',
                },
                pl: {
                    xs: 2,
                    md: 3,
                },
                pr: {
                    xs: 1,
                    md: 3,
                },
                fontSize: {
                    xs: '12px',
                    md: '16px',
                },
                '>.MuiBox-root:nth-child(1)': {
                    width: {
                        xs: '66%',
                        md: '60%',
                    },
                },
                '>.MuiBox-root:nth-child(2)': {
                    width: {
                        xs: '22%',
                        md: '20%',
                    },
                },
                '>.MuiBox-root:nth-child(3)': {
                    width: {
                        xs: '22%',
                        md: '20%',
                    },
                },
                svg: {
                    flexShrink: '0',
                    flexBasis: {
                        xs: '16px',
                        md: '24px',
                    },
                    width: {
                        xs: '16px',
                        md: '24px',
                    },
                    height: {
                        xs: '16px',
                        md: '24px',
                    },
                },
                ...(!header && {
                    '&:not(:last-of-type)': {
                        borderBottom: '1px solid rgba(224, 224, 224, 1)',
                    },
                    '.moving-emoji': {
                        mr: 0,
                        transition: 'margin-right .2s ease-in-out',
                    },
                    ':hover': {
                        backgroundColor: palette.Grey100,
                        '.moving-emoji': {
                            mr: 1,
                        },
                    },
                }),
            }}
        >
            {children}
        </Box>
    )
}

const EcoTransITVsLuneTable = () => {
    const { smScreen } = useDevice()

    return (
        <FullScreenOnMobileWrapper>
            <Box>
                <Text
                    variant={smScreen ? 'h6' : 'h4'}
                    sx={{
                        display: 'inline-block',
                        textAlign: 'center',
                        width: '100%',
                        mb: {
                            xs: 4,
                            sm: 9,
                        },
                    }}
                >
                    Why do our customers choose Lune over EcoTransIT?
                </Text>
                <Box
                    sx={{
                        ...(!smScreen
                            ? {
                                  border: '1px solid rgba(224, 224, 224, 1)',
                                  borderRadius: '40px',
                              }
                            : {
                                  borderBottom: '1px solid rgba(224, 224, 224, 1)',
                              }),
                    }}
                >
                    <TableRow header={true}>
                        <Box>Feature</Box>
                        <Box>EcoTransIT</Box>
                        <Box>Lune</Box>
                    </TableRow>
                    {tableContent.map(({ lune, ecotransit, feature }, index) => (
                        <TableRow key={`compare-row-${index}`}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'flex-start',
                                    alignItems: 'center',
                                    gap: {
                                        xs: 1,
                                        md: 3,
                                    },
                                }}
                            >
                                <Box className={'moving-emoji'}>{feature.emoji}</Box>
                                {feature.label}
                            </Box>
                            <Box>{ecotransit}</Box>
                            <Box>{lune}</Box>
                        </TableRow>
                    ))}
                </Box>
            </Box>
        </FullScreenOnMobileWrapper>
    )
}

export default EcoTransITVsLuneTable
